import type { UnifiedProgramSessionResponse } from "@/libs/programsApi";

export function formatDate(value: string | null | undefined, timeZone: string): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone,
  });
}

// ---------------------------------------------------------------------------
// Sessions section helpers — all timezone rendering is done with Intl using the
// PROGRAM's IANA zone (never the browser's). Sessions, times, and tracks come
// from the backend; the only client date math is computing the visible month's
// window bounds for the query.
// ---------------------------------------------------------------------------

// The visible calendar month, tracked as a year/month pair so navigation is a
// pure integer step (no Date arithmetic across DST). Month is 0-indexed.
export interface VisibleMonth {
  year: number;
  month: number;
}

// The UTC offset (in minutes) that `timeZone` is from UTC at the given instant.
// Positive east of UTC (e.g. Tokyo +540), negative west (e.g. LA -420/-480).
// Used to translate a program-tz wall-clock time into the matching UTC instant.
function zoneOffsetMinutes(date: Date, timeZone: string): number {
  try {
    // Read the wall-clock fields the zone shows for this instant, reinterpret
    // them AS IF they were UTC, and diff against the real instant. The gap is
    // the zone's offset at that moment (DST-aware).
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: false,
    }).formatToParts(date);
    const f = (t: string) => Number(parts.find((p) => p.type === t)?.value);
    let hour = f("hour");
    if (hour === 24) hour = 0; // Intl can emit "24" for midnight
    const asUtc = Date.UTC(
      f("year"),
      f("month") - 1,
      f("day"),
      hour,
      f("minute"),
      f("second"),
    );
    return Math.round((asUtc - date.getTime()) / 60000);
  } catch {
    return 0;
  }
}

// The UTC instant for a wall-clock moment (y/m/d 0-indexed month) IN `timeZone`.
// Iterates once to settle DST: the offset can differ between a naive guess and
// the actual local instant, so we recompute the offset at the guessed instant.
function zonedWallTimeToUtc(
  year: number,
  month: number,
  day: number,
  timeZone: string,
): Date {
  const naiveUtc = Date.UTC(year, month, day, 0, 0, 0);
  const guess = new Date(naiveUtc);
  const offset = zoneOffsetMinutes(guess, timeZone);
  // local = utc + offset  ⇒  utc = local - offset. We want the UTC instant whose
  // local wall-clock is midnight (the naive value), so subtract the offset.
  const utc = naiveUtc - offset * 60000;
  return new Date(utc);
}

// Window bounds for a month: [first instant of the month, first instant of the
// next month) IN THE PROGRAM TIMEZONE, expressed as UTC ISO strings. Sessions
// are grouped/labeled in the program tz, so the query window must be the
// program-tz month edges — computing UTC month edges would drop sessions in the
// last/first local hours of the month for far-from-UTC zones (off-by-one).
export function monthWindow(
  vm: VisibleMonth,
  timeZone: string,
): { windowStart: string; windowEnd: string } {
  const start = zonedWallTimeToUtc(vm.year, vm.month, 1, timeZone);
  const nextMonth = vm.month === 11 ? 0 : vm.month + 1;
  const nextYear = vm.month === 11 ? vm.year + 1 : vm.year;
  const end = zonedWallTimeToUtc(nextYear, nextMonth, 1, timeZone);
  return { windowStart: start.toISOString(), windowEnd: end.toISOString() };
}

export function stepMonth(vm: VisibleMonth, delta: number): VisibleMonth {
  const total = vm.year * 12 + vm.month + delta;
  return { year: Math.floor(total / 12), month: ((total % 12) + 12) % 12 };
}

// Start dates are instants. Open the month containing that instant in the
// program's timezone, consistently with its displayed schedule dates.
export function initialVisibleMonth(
  startDate: string | null | undefined,
  timeZone: string,
): VisibleMonth {
  const d = startDate ? new Date(startDate) : new Date();
  const base = Number.isNaN(d.getTime()) ? new Date() : d;
  const { year, month } = ymdInZone(base, timeZone);
  return { year, month };
}

// Decompose an instant into year / 0-indexed month / day AS SEEN in `timeZone`.
function ymdInZone(
  date: Date,
  timeZone: string,
): { year: number; month: number; day: number } {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "numeric",
      day: "numeric",
    }).formatToParts(date);
    const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
    return { year: get("year"), month: get("month") - 1, day: get("day") };
  } catch {
    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth(),
      day: date.getUTCDate(),
    };
  }
}

// "Jul 6" — the session's calendar day rendered in the program timezone. Used
// both as the group key and the group's visible label.
export function formatDayLabel(iso: string, timeZone: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone,
      month: "short",
      day: "numeric",
    }).format(d);
  } catch {
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }
}

// A stable per-day key in the program timezone (e.g. "2026-07-06") so two
// instants on the same local day group together even across a UTC date line.
export function dayKey(iso: string, timeZone: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const { year, month, day } = ymdInZone(d, timeZone);
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

// A datetime-local wall-clock string ("2026-07-06T13:00") interpreted IN
// `timeZone`, returned as the absolute UTC instant. The operator types/reads a
// program-tz wall clock; the API needs a UTC instant. We parse the wall-clock
// fields, guess the UTC instant naively, then settle the zone offset at that
// guess (DST-aware) — same one-iteration settle as zonedWallTimeToUtc. Returns
// null for an unparseable/empty input so callers can guard before POSTing.
export function datetimeLocalToUtc(
  value: string,
  timeZone: string,
): Date | null {
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return null;
  const [, ys, mos, ds, hs, mins] = m;
  const year = Number(ys);
  const month = Number(mos) - 1; // 0-indexed
  const day = Number(ds);
  const hour = Number(hs);
  const minute = Number(mins);
  const naiveUtc = Date.UTC(year, month, day, hour, minute, 0);
  if (Number.isNaN(naiveUtc)) return null;
  const offset = zoneOffsetMinutes(new Date(naiveUtc), timeZone);
  // local = utc + offset ⇒ utc = local - offset.
  const utc = new Date(naiveUtc - offset * 60000);
  return Number.isNaN(utc.getTime()) ? null : utc;
}

// "10:00 AM" — a single instant rendered as a time in the program timezone.
function formatTime(iso: string, timeZone: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour: "numeric",
      minute: "2-digit",
    }).format(d);
  } catch {
    return d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  }
}

// "10:00 AM – 11:30 AM" in the program timezone.
export function formatTimeRange(
  startIso: string,
  endIso: string,
  timeZone: string,
): string {
  return `${formatTime(startIso, timeZone)} – ${formatTime(endIso, timeZone)}`;
}

// A session is renderable only if BOTH its start_time and end_time are present
// AND parse to a real instant. This explicitly rejects null / "" / garbage —
// NOT just "Invalid Date": `new Date(null)` is the Unix epoch (a VALID date), so
// a plain NaN guard would let a null-time row fabricate an epoch time/day. Bad
// rows are dropped silently rather than inventing data (content-copy rule).
export function isRenderableSession(s: UnifiedProgramSessionResponse): boolean {
  if (!s.start_time || !s.end_time) return false;
  return (
    !Number.isNaN(new Date(s.start_time).getTime()) &&
    !Number.isNaN(new Date(s.end_time).getTime())
  );
}

// "July 2026" — the visible-month label for the navigator.
export function monthLabel(vm: VisibleMonth): string {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: "UTC",
      month: "long",
      year: "numeric",
    }).format(new Date(Date.UTC(vm.year, vm.month, 1)));
  } catch {
    return `${vm.year}-${vm.month + 1}`;
  }
}
